import {
  Dialog as BlueprintDialog,
  Button,
  DialogBody,
  DialogFooter,
  Icon,
} from '@blueprintjs/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import { useRef, useState } from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import { QUERY_KEYS } from '~/api/query-client-keys'
import CertificateOfAnalysis from '~/components/CertificateOfAnalysis'
import Dialog from '~/components/Dialog'
import Link from '~/components/Link'
import { ScrollArea } from '~/components/ui/scroll-area'
import { Skeleton } from '~/components/ui/skeleton'

// Import SCSS for dialog styles
import '~/components/CertificateOfAnalysis/CertificateOfAnalysis.scss'

export interface ImageFilters {
  brightness: number
  contrast: number
  saturation: number
  hueRotate: number
  blur: number
  grayscale: number
  sepia: number
  invert: number
}

export const DEFAULT_FILTERS: ImageFilters = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hueRotate: 0,
  blur: 0,
  grayscale: 0,
  sepia: 0,
  invert: 0,
}

interface ImageManipulationControlsProps {
  filters: ImageFilters
  onFiltersChange: (filters: ImageFilters) => void
  onResetFilters?: () => void
  onShareURL?: () => Promise<boolean>
  plateBarcode: string
  well: string
  observationNumber: number
  isVisible?: boolean
  stitchImage?: boolean
  onStitchImageChange?: (stitched: boolean) => void
  cultureId?: string
  observationId?: string
}

export function RepresentativeImages({
  filters: _filters,
  onFiltersChange: _onFiltersChange,
  onResetFilters: _onResetFilters,
  onShareURL: _onShareURL,
  isVisible = true,
  plateBarcode: _plateBarcode,
  well: _well,
  stitchImage: _stitchImage,
  onStitchImageChange: _onStitchImageChange,
  observationNumber: _observationNumber,
  cultureId,
  observationId: _observationId,
}: ImageManipulationControlsProps) {
  const client = useBackendClient()
  const queryClient = useQueryClient()
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<{
    url: string
    plate: string
    well: string
    timestamp: string
    confluence: number | null
  } | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [pendingDeleteImageId, setPendingDeleteImageId] = useState<string | null>(null)
  const [showCertificateDialog, setShowCertificateDialog] = useState(false)
  const [isExportingCertificate, setIsExportingCertificate] = useState(false)
  const certificateRef = useRef<HTMLDivElement>(null)

  // Fetch representative images for the culture lineage
  const { data, isLoading, error } = useQuery({
    queryFn: async () => {
      if (!cultureId) {
        return null
      }
      return await client.representativeImages.getCultureLineageRepresentativeImages(
        cultureId,
      )
    },
    queryKey: [QUERY_KEYS.REPRESENTATIVE_IMAGES, cultureId],
    enabled: isVisible && !!cultureId,
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (imageId: string) => {
      await client.representativeImages.deleteRepresentativeImage(imageId)
    },
    onSuccess: () => {
      // Invalidate and refetch the representative images
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.REPRESENTATIVE_IMAGES],
      })
    },
    onError: error => {
      console.error('Failed to delete representative image:', error)
    },
  })

  const handleExportCertificate = async () => {
    if (!certificateRef.current) return

    try {
      setIsExportingCertificate(true)

      // Use html2canvas to capture the certificate
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2, // High resolution
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#f5f5f5',
        logging: false,
        imageTimeout: 15000,
      })

      // Convert to blob and download
      canvas.toBlob(
        blob => {
          if (blob) {
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `certificate-of-analysis-${dayjs().format('YYYY-MM-DD-HHmmss')}.png`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
          }
        },
        'image/png',
        1.0,
      )
    } catch (error) {
      console.error('Failed to export certificate:', error)
    } finally {
      setIsExportingCertificate(false)
    }
  }

  if (!isVisible) return null

  const images = data?.lineage_images || []

  // Group images by plate_barcode and sort by observation_timestamp within each plate
  const imagesByPlate = images.reduce(
    (acc, image) => {
      if (!acc[image.plate_barcode]) {
        acc[image.plate_barcode] = []
      }
      acc[image.plate_barcode].push(image)
      return acc
    },
    {} as Record<string, typeof images>,
  )

  // Sort images within each plate by observation_timestamp (earlier first)
  for (const plateBarcode of Object.keys(imagesByPlate)) {
    imagesByPlate[plateBarcode].sort(
      (a, b) =>
        new Date(a.observation_timestamp).getTime() -
        new Date(b.observation_timestamp).getTime(),
    )
  }

  // Get plates in order (they should already be in lineage order from the API)
  const plateOrder = Object.keys(imagesByPlate)

  // Create a flattened sorted array for the Certificate of Analysis
  const sortedImages = plateOrder.flatMap(plateBarcode => imagesByPlate[plateBarcode])

  if (isLoading) {
    return (
      <div className='w-full h-full flex flex-col overflow-hidden '>
        <ScrollArea className='flex-1 w-full min-h-0'>
          <div className='p-4'>
            <div className='grid grid-cols-2 gap-6'>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className='flex flex-col gap-4'>
                  <Skeleton className='h-4 w-24' />
                  <Skeleton className='w-full aspect-square' />
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-full h-full flex flex-col overflow-hidden '>
        <div className='p-4'>
          <div className='text-red-500'>Failed to load representative images</div>
        </div>
      </div>
    )
  }

  if (images.length === 0) {
    return (
      <div className='w-full h-full flex flex-col overflow-hidden'>
        <div className='p-4'>
          <div className='text-gray-500'>
            No representative images saved yet. Use the "Save as Representative" button
            in the Image Tools tab to save the current view.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='w-full flex flex-col overflow-hidden h-[800px]'>
      {/* Generate Certificate Button */}
      <div className='p-4 border-b border-gray-200'>
        <Button
          icon={<Icon icon='document' />}
          onClick={() => setShowCertificateDialog(true)}
          disabled={images.length === 0}
        >
          Generate Certificate of Analysis
        </Button>
      </div>

      <ScrollArea className='flex-1 w-full'>
        <div className='p-4'>
          <div className='flex flex-col gap-8'>
            {plateOrder.map(plateBarcode => (
              <div key={plateBarcode} className='flex flex-col gap-2'>
                {/* Plate header */}
                <div className='mb-2'>
                  <h3 className='text-sm font-semibold text-gray-700'>
                    {plateBarcode}
                  </h3>
                </div>
                {/* Images grid for this plate */}
                <div className='grid grid-cols-2 gap-6'>
                  {imagesByPlate[plateBarcode].map(image => (
                    <div key={image.image_id} className='flex flex-col gap-2 min-w-0'>
                      <div className='flex flex-col gap-1 min-w-0'>
                        <div
                          className='text-xs text-gray-600 truncate'
                          title={image.well}
                        >
                          {image.well}
                        </div>
                        <div
                          className='text-xs text-gray-600 truncate'
                          title={dayjs(image.observation_timestamp).format(
                            'MMM D, YYYY h:mm A',
                          )}
                        >
                          {dayjs(image.observation_timestamp).format(
                            'MMM D, YYYY h:mm A',
                          )}
                        </div>
                        <div className='flex justify-between items-center min-w-0'>
                          {image.confluence !== null && (
                            <div className='text-xs text-gray-600 truncate'>
                              Confluence: {Math.round(image.confluence)}%
                            </div>
                          )}
                          <div className='flex gap-1 ml-auto flex-shrink-0'>
                            <Link
                              to={`/plate/${image.plate_extid}/well/${image.well}?t=${new Date(image.observation_timestamp).toISOString()}&tab=image-tools`}
                              className='bp5-button bp5-minimal bp5-small'
                            >
                              <span title='Edit in image tools'>
                                <Icon icon='edit' />
                              </span>
                            </Link>
                            <Button
                              icon={<Icon icon='trash' />}
                              intent='danger'
                              small
                              minimal
                              onClick={() => {
                                setPendingDeleteImageId(image.image_id)
                                setShowDeleteDialog(true)
                              }}
                              disabled={deleteMutation.isPending}
                              title='Delete representative image'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='w-full aspect-square'>
                        <img
                          alt=''
                          src={image.s3_url}
                          className='w-full h-full object-cover rounded border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer'
                          crossOrigin='anonymous'
                          onClick={() => {
                            setSelectedImage({
                              url: image.s3_url,
                              plate: image.plate_barcode,
                              well: image.well,
                              timestamp: image.observation_timestamp,
                              confluence: image.confluence,
                            })
                            setViewDialogOpen(true)
                          }}
                          title='Click to enlarge'
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </ScrollArea>

      <Dialog
        isOpen={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        className='representative-image-view-dialog'
      >
        {selectedImage && (
          <>
            <Dialog.Title>{selectedImage.plate}</Dialog.Title>
            <Dialog.Subtitle>{selectedImage.well}</Dialog.Subtitle>
            <Dialog.Info>
              {dayjs(selectedImage.timestamp).format('MMM D, YYYY h:mm A')}
              {selectedImage.confluence !== null &&
                ` · Confluence: ${Math.round(selectedImage.confluence)}%`}
            </Dialog.Info>
            <div className='flex items-center justify-center mt-4'>
              <img
                src={selectedImage.url}
                className='max-w-full max-h-[60vh] object-contain'
                crossOrigin='anonymous'
                alt={`${selectedImage.plate} ${selectedImage.well}`}
              />
            </div>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false)
          setPendingDeleteImageId(null)
        }}
      >
        <Dialog.Title>Delete Representative Image?</Dialog.Title>
        <Dialog.Info>
          Are you sure you want to delete this representative image? This action cannot
          be undone.
        </Dialog.Info>
        <Dialog.Footer align='right'>
          <Dialog.FooterButton
            label='Cancel'
            onClick={() => {
              setShowDeleteDialog(false)
              setPendingDeleteImageId(null)
            }}
            type='normal'
            disabled={deleteMutation.isPending}
          />
          <Dialog.FooterButton
            label='Delete'
            onClick={() => {
              if (pendingDeleteImageId) {
                deleteMutation.mutate(pendingDeleteImageId, {
                  onSettled: () => {
                    setShowDeleteDialog(false)
                    setPendingDeleteImageId(null)
                  },
                })
              }
            }}
            type='danger'
            disabled={deleteMutation.isPending}
          />
        </Dialog.Footer>
      </Dialog>

      {/* Certificate of Analysis Dialog */}
      <BlueprintDialog
        isOpen={showCertificateDialog}
        onClose={() => setShowCertificateDialog(false)}
        title='Certificate of Analysis Preview'
        className='certificate-of-analysis-dialog'
      >
        <DialogBody>
          <CertificateOfAnalysis ref={certificateRef} images={sortedImages} />
        </DialogBody>
        <DialogFooter
          actions={
            <>
              <Button onClick={() => setShowCertificateDialog(false)}>Cancel</Button>
              <Button
                intent='primary'
                onClick={handleExportCertificate}
                disabled={isExportingCertificate}
              >
                {isExportingCertificate ? 'Exporting...' : 'Export as Image'}
              </Button>
            </>
          }
        />
      </BlueprintDialog>
    </div>
  )
}
